import React, { useMemo } from 'react';
import { Microscope } from 'lucide-react';
import { UseMutationResult } from '@tanstack/react-query';

import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import SidebarHeader from '@/components/ui/SidebarHeader';
import ProjectSelectionDropdown from '@/components/image-generator/ImageGeneration/ProjectSelectionDropdown';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useResearchContext } from '@/contexts/research/ResearchContext';
import { useResearchSubmission } from './hooks/useResearchSubmission';
import ResearchAssistantContent from './components/ResearchAssistantContent';
import { useResearchGeneration } from '@/hooks/research-generator';
import { usePrivateResearchGeneration } from '@/hooks/private-research-generator/usePrivateResearchGeneration';

interface ResearchAssistantProps {
  onGenerateResearch: () => void;
  onResearchGenerated?: (research: any) => void;
  generateResearchMutation?: UseMutationResult<any, Error, any, unknown>;
  selectedProjectId?: string | null;
  onProjectChange?: (projectId: string | null) => void;
}

const ResearchAssistant: React.FC<ResearchAssistantProps> = React.memo(({ 
  onGenerateResearch,
  onResearchGenerated,
  generateResearchMutation,
  selectedProjectId,
  onProjectChange
}) => {
  const { isMobile } = useIsMobile();
  const { state, actions } = useResearchContext();
  
  // Use the provided mutation or fall back to private (for backward compatibility)
  const mutation = generateResearchMutation || usePrivateResearchGeneration();
  
  const { handleGenerateResearch, isReadyToGenerate, isLoading } = useResearchSubmission({
    state,
    onGenerateResearch,
    onResearchGenerated,
    resetForm: () => {
      // Clear ALL filter states, not just prompt and selected filters
      actions.resetState();
    },
    generateResearchMutation: mutation,
    selectedProjectId,
  });

  // Memoize handlers object to prevent unnecessary re-renders
  const handlers = useMemo(() => ({
    handlePromptChange: actions.setResearchPrompt,
    handleTypeChange: actions.setResearchType,
    handleTimeframeChange: actions.setTimeframe,
    handleCountryChange: actions.setCountry,
    handleSourceTypeChange: actions.setSourceType, // Updated from handleSourceQualityChange
    // handleFiltersChange removed - no longer supporting dynamic filters
    handleFiltersChange: () => {}, // Keep for interface compatibility
    handleSimpleModeToggle: actions.setSimpleMode,
    handleExpandType: () => {}, // Keep for interface compatibility but not used
    resetForm: () => {
      // Clear ALL filter states, not just prompt and selected filters
      actions.resetState();
    },
  }), [actions]);

  return (
    <MobileSidebarAccordion
      variant="purple"
      persistKey="research-assistant-sidebar"
      defaultExpanded={false}
      className="h-full"
    >
      <SidebarHeader 
        title="Create Research" 
        icon={Microscope} 
        variant="purple"
      />
      
      {/* Project Selection Dropdown */}
      {onProjectChange && (
        <ProjectSelectionDropdown
          selectedProjectId={selectedProjectId}
          onProjectChange={onProjectChange}
        />
      )}
      
      <ResearchAssistantContent
        state={state}
        handlers={handlers}
        onGenerate={handleGenerateResearch}
        isReady={isReadyToGenerate}
        isLoading={isLoading}
      />
    </MobileSidebarAccordion>
  );
});

ResearchAssistant.displayName = 'ResearchAssistant';

export default ResearchAssistant;
